import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ArrowRight, CalendarClock, Gauge, Mail, RefreshCw } from "lucide-react";
import { fmtDate, STATUS_LABELS } from "@/lib/format";
import { ClientContactPicker } from "@/components/ClientContactPicker";
import StateCountyPicker from "@/components/StateCountyPicker";
import { ScopeEditor } from "@/components/ScopeEditor";
import { SimilarProjectsWarning } from "@/components/SimilarProjectsWarning";
import { JourneyStepper } from "@/components/JourneyStepper";
import { BidTeamChip } from "@/components/BidTeam";
import { lifecycleStage } from "@/lib/lifecycle";
import ActivityPanel from "@/components/ActivityPanel";
import { DraftsCard } from "@/components/claude/EmailDrafts";

const SOURCES = [["website", "Website / RocketFuel"], ["email_rfp", "Email RFP"], ["referral", "Referral"], ["linkedin", "LinkedIn"], ["other", "Other"]];

// Emails linked to a lead (its own per-project Gmail query), with a manual re-scan.
function LeadEmailPanel({ leadId }) {
  const [ctx, setCtx] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const load = () => api.get(`/leads/${leadId}/context`).then((r) => setCtx(r.data)).catch(() => setCtx(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);
  const sync = async () => {
    setSyncing(true);
    try {
      await api.post(`/leads/${leadId}/email/sync`);
      toast.success("Scanning your inbox for this lead — new mail links in a moment");
      setTimeout(() => { load(); setSyncing(false); }, 5000);
    } catch (e) { toast.error(e.response?.data?.detail || "Connect Gmail in the Inbox first"); setSyncing(false); }
  };
  const rs = ctx?.reply_state;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5" data-testid="lead-email-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700"><Mail className="w-3.5 h-3.5 text-[#003E87]" /> Emails {ctx ? `(${ctx.count})` : ""}</div>
        <Button size="sm" variant="ghost" onClick={sync} disabled={syncing} data-testid="lead-email-sync"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Scanning…" : "Sync"}</Button>
      </div>
      {rs?.reason && <div className={`text-[11px] mt-1 ${rs.owe ? "text-[#8a6d00]" : "text-slate-400"}`}>{rs.reason}</div>}
      <div className="mt-2 space-y-1 max-h-40 overflow-auto">
        {(ctx?.items || []).length === 0 && <div className="text-[11px] text-slate-400">No email linked yet. Hit Sync to pull this lead's threads from your inbox by its name.</div>}
        {(ctx?.items || []).map((e) => (
          <div key={e.id} className="flex items-start gap-2 text-[11px]" data-testid={`lead-email-${e.id}`}>
            <span className={`mt-0.5 shrink-0 ${e.direction === "in" ? "text-emerald-600" : "text-slate-400"}`}><Mail className="w-3 h-3" /></span>
            <div className="min-w-0">
              <div className="text-slate-700 truncate">{e.subject || e.summary || "(no subject)"}</div>
              <div className="text-slate-400 truncate">{e.counterparty}{e.date ? ` · ${fmtDate(e.date)}` : ""}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// New-lead fast capture.
export function LeadCaptureDialog({ open, onOpenChange, form, setForm, saving, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lead-capture-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>New lead</DialogTitle>
          <DialogDescription>Capture fast. Only a project name or a company is required — qualify and price it later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <ClientContactPicker
            clientId={form.client_id} clientName={form.company}
            contactId={form.contact_id} contactName={form.contact_name}
            onClient={(c) => setForm((s) => ({ ...s, client_id: c.client_id, company: c.name, contact_id: null, contact_name: "", contact_email: "", contact_phone: "" }))}
            onContact={(ct) => setForm((s) => ({ ...s, contact_id: ct.contact_id, contact_name: ct.name, contact_email: ct.email, contact_phone: ct.phone }))}
          />
          <SimilarProjectsWarning name={form.project_name} company={form.company} onClose={() => onOpenChange(false)} />
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Project name</Label>
              <Input data-testid="lead-project" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} placeholder="Panhandle 150MW" /></div>
            <div><Label className="text-xs">Source</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger data-testid="lead-source"><SelectValue placeholder="How did it come in?" /></SelectTrigger>
                <SelectContent>{SOURCES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div>
            <Label className="text-xs">Scopes <span className="text-rose-500">*</span></Label>
            <div className="mt-1.5"><ScopeEditor value={form.scopes || []} onChange={(next) => setForm({ ...form, scopes: next })} /></div>
          </div>
          <StateCountyPicker idPrefix="lead" state={form.state} county={form.county}
            onChange={({ state, county }) => setForm({ ...form, state, county })} />
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Capacity (MW)</Label>
              <Input data-testid="lead-mw" type="number" value={form.capacity_mw} onChange={(e) => setForm({ ...form, capacity_mw: e.target.value })} /></div>
            <div><Label className="text-xs">Due date</Label>
              <Input data-testid="lead-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            <div><Label className="text-xs">Mobilization</Label>
              <Input data-testid="lead-mobilization" type="date" value={form.mobilization_date} onChange={(e) => setForm({ ...form, mobilization_date: e.target.value })} /></div>
          </div>
          <div><Label className="text-xs">Notes</Label>
            <Textarea data-testid="lead-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything worth remembering about this pursuit." /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="lead-capture-submit" onClick={onSubmit} disabled={saving} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold">Save lead</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Lead detail: edit -> qualify -> convert.
export function LeadDetailDialog({ lead, onClose, leadEdit, setLeadEdit, gaps, onSave, onConvert, onDecide, onActivityChange }) {
  return (
    <Dialog open={!!lead} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="lead-detail-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {lead?.project_name || lead?.company || "Lead"}
            <span className="text-[10px] font-semibold uppercase tracking-wide text-[#003E87] bg-[#003E87]/10 rounded px-1.5 py-0.5">{STATUS_LABELS[lead?.status] || lead?.status}</span>
            {lead?.lead_id && <BidTeamChip bidId={lead.lead_id} kind="leads" compact onChanged={onActivityChange} />}
          </DialogTitle>
          <DialogDescription>
            {lead?.status === "qualified"
              ? "Pursuing — the bid decision is made. Convert to spawn a linked estimate."
              : lead?.status === "no_bid"
              ? "Marked no-bid. Re-open the decision if something has changed."
              : lead?.status === "review"
              ? "Flagged for review. Finish the bid decision to move it forward."
              : "New intake. The next step is the bid decision — go, no-go, or review."}
          </DialogDescription>
        </DialogHeader>
        {lead && (
          <div className="mb-1 rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3">
            <JourneyStepper item={{ kind: "lead", status: lead.status }} stage={lifecycleStage({ kind: "lead", status: lead.status })} />
          </div>
        )}
        {lead && (
          <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Company</Label>
                <Input value={leadEdit.company} onChange={(e) => setLeadEdit({ ...leadEdit, company: e.target.value })} /></div>
              <div><Label className="text-xs">Project name</Label>
                <Input value={leadEdit.project_name} onChange={(e) => setLeadEdit({ ...leadEdit, project_name: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Contact</Label>
                <Input value={leadEdit.contact_name} onChange={(e) => setLeadEdit({ ...leadEdit, contact_name: e.target.value })} /></div>
              <div><Label className="text-xs">Email</Label>
                <Input value={leadEdit.contact_email} onChange={(e) => setLeadEdit({ ...leadEdit, contact_email: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className={`text-xs ${!leadEdit.capacity_mw ? "text-amber-600" : ""}`}>Capacity (MW) *</Label>
                <Input data-testid="lead-edit-mw" type="number" value={leadEdit.capacity_mw} onChange={(e) => setLeadEdit({ ...leadEdit, capacity_mw: e.target.value })} /></div>
              <div><Label className={`text-xs ${!leadEdit.due_date ? "text-amber-600" : ""}`}>Target bid date *</Label>
                <Input data-testid="lead-edit-due" type="date" value={leadEdit.due_date} onChange={(e) => setLeadEdit({ ...leadEdit, due_date: e.target.value })} /></div>
            </div>
            <StateCountyPicker idPrefix="lead-edit" required state={leadEdit.state} county={leadEdit.county}
              onChange={({ state, county }) => setLeadEdit({ ...leadEdit, state, county })} />
            <div>
              <Label className="text-xs">Scopes</Label>
              <div className="mt-1.5">
                <ScopeEditor value={leadEdit.scopes || []} onChange={(next) => setLeadEdit({ ...leadEdit, scopes: next })} />
              </div>
            </div>
            <div><Label className="text-xs">Mobilization date</Label>
              <Input data-testid="lead-edit-mobilization" type="date" value={leadEdit.mobilization_date}
                onChange={(e) => setLeadEdit({ ...leadEdit, mobilization_date: e.target.value })} />
              <p className="text-[11px] text-slate-400 mt-1">Anchors the construction schedule. Location &amp; this date carry into the estimate.</p></div>
            {leadEdit.mobilization_date && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2" data-testid="lead-timeline">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700"><CalendarClock className="w-3.5 h-3.5 text-[#003E87]" /> Project timeline</div>
                <div className="text-[11px] text-slate-500 mt-1">Mobilizes {fmtDate(leadEdit.mobilization_date)}. The full construction window appears here once the estimate is priced and scheduled.</div>
              </div>
            )}
            <div><Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={leadEdit.notes} onChange={(e) => setLeadEdit({ ...leadEdit, notes: e.target.value })} /></div>
            {lead.bid_decision && (
              <div data-testid="lead-decision-summary" className="text-xs rounded-lg border border-slate-200 bg-white px-3 py-2">
                <div className="flex items-center gap-2">
                  <Gauge className="w-3.5 h-3.5 text-[#003E87]" />
                  <span className="font-semibold text-black">Bid decision: {lead.bid_decision.band_label}</span>
                  {lead.bid_decision.overall != null && <span className="font-mono text-slate-500">fit {lead.bid_decision.overall}</span>}
                  <span className="text-slate-400 ml-auto">{lead.bid_decision.decided_by}</span>
                </div>
                {lead.bid_decision.rationale && <div className="text-slate-500 mt-1">"{lead.bid_decision.rationale}"</div>}
              </div>
            )}
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5" data-testid="lead-activity-panel">
              <ActivityPanel entityType="lead" entityId={lead.lead_id} onChange={onActivityChange} />
            </div>
            <LeadEmailPanel leadId={lead.lead_id} />
            <DraftsCard leadId={lead.lead_id} />
            {gaps.length > 0 && lead.status === "qualified" && (
              <div className="text-xs text-amber-700 bg-amber-50 rounded px-2.5 py-1.5">Fill before converting: {gaps.join(", ")}.</div>
            )}
          </div>
        )}
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" data-testid="lead-save-btn" onClick={onSave}>Save</Button>
          {lead?.status === "qualified" ? (
            <Button data-testid="lead-convert-btn" onClick={onConvert} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold">
              Convert to estimate <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          ) : lead?.status === "no_bid" ? (
            <Button data-testid="lead-reopen-btn" onClick={() => onDecide(lead)} variant="outline" className="border-[#003E87]/30 text-[#003E87] hover:bg-[#003E87]/5">
              <Gauge className="w-4 h-4 mr-1.5" /> Re-open decision
            </Button>
          ) : (
            <Button data-testid="lead-decide-btn" onClick={() => onDecide(lead)} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold">
              <Gauge className="w-4 h-4 mr-1.5" /> {lead?.status === "review" ? "Finish bid decision" : "Run bid decision"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
